import { Lang } from "@/lib/i18n";
import { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Woven Wire Mesh",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Stainless Steel Filter Cloth",
      lead: "MutualFilter manufactures precision stainless steel dutch-woven filter cloth engineered for critical solid-liquid separation, high differential pressure environments, and continuous polymer extrusion. High-density wire arrangements produce tortuous micro-pore geometry with zero direct line of sight.",
      secondaryCta: {
        label: "View Woven Wire Mesh",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "Stainless steel dutch woven filter cloth roll and mesh surface",
      },
      cardNote: "Dutch weave filtration media",
      cardLink: {
        label: "Technical Specs",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "Nominal Rating", value: "1 µm – 310 µm" },
        { label: "Weave Patterns", value: "Plain, Twill & Reverse Dutch" },
        { label: "Standard Alloys", value: "AISI 304, 304L, 316, 316L" },
        { label: "Compliance Standards", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "Structural Filtration Media",
      heading: "Engineered Dutch Weaves for Precise Micro-Retention",
      paragraphs: [
        "Stainless steel filter cloth utilizes unequal warp and shute wire diameters with high-density wire packing. Unlike standard square weave mesh where aperture dimensions correspond directly to open spaces between wires, Dutch weaves force process fluids through triangular, wedge-shaped pore channels formed between overlapping wires.",
        "This dense mechanical construction eliminates direct line-of-sight apertures, preventing particulate bleed-through even under elevated operating pressures. The substantial mechanical mass of heavy warp wires resists deformation, maintaining strict dimensional stability across varying viscosity and hydraulic cycles.",
        "MutualFilter supplies industrial filter cloth in continuous master rolls, precision slit coils, multi-layer sintered composites, and custom-stamped circular discs tailored for pressure leaf filters, candle filters, horizontal plate filters, and automated polymer melt screen changers.",
      ],
      keyPoints: [
        "Tortuous micro-channels provide high particulate retention with zero direct line of sight.",
        "Heavy warp and compacted weft construction resists deformation under high differential pressure.",
        "Available in master rolls, custom slit ribbons, and precision stamped filter discs.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Macro view of stainless steel filter cloth weave structure",
      },
    },
    specTable: {
      heading: "Dutch Weave Filter Cloth Specifications Overview",
      intro: "Standard manufacturing parameters for industrial stainless steel dutch-woven filter cloth across primary weave categories.",
      columns: [
        { key: "weaveType", label: "Weave Type", align: "left" },
        { key: "meshRange", label: "Standard Mesh Range", align: "right" },
        { key: "retention", label: "Nominal Retention", align: "right" },
        { key: "poreStructure", label: "Pore Geometry", align: "left" },
        { key: "standard", label: "Standard", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "Plain Dutch Weave",
          meshRange: "8×85 – 80×400 (to 165×1200)",
          retention: "~20 – 310 µm nominal",
          poreStructure: "Uniform triangular micronic openings",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "Twill Dutch Weave",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm nominal",
          poreStructure: "2/2 dense twilled double-weft packing",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "Reverse Dutch Weave",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm nominal",
          poreStructure: "Compacted warp with high longitudinal tensile strength",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "Values represent standard nominal retention and dimensional specifications compliant with ISO 9044, ASTM E2016, and ASTM E2814.",
    },
    features: {
      heading: "Key Engineering Attributes",
      intro: "Critical structural characteristics that distinguish industrial dutch-woven filter cloth from standard square wire mesh.",
      items: [
        {
          title: "Rigid Structural Integrity",
          text: "Heavy base wires create a robust mechanical framework capable of sustaining demanding hydraulic differential pressure without aperture enlargement.",
        },
        {
          title: "Controlled Retention Rating",
          text: "Precision winding and compacting ensure uniform micro-apertures across the entire cloth surface for dependable cutoff ratings from 1 µm to 310 µm.",
        },
        {
          title: "Superior Cake Discharge",
          text: "Smooth surface profiles in plain and twill Dutch weaves allow clean cake release during backwashing, manual scraping, or pressure gas pulse regeneration.",
        },
        {
          title: "Corrosion-Resistant Alloys",
          text: "Woven from premium AISI 304, 304L, 316, and 316L stainless steel wires for chemical compatibility in neutral and aggressive liquid systems.",
        },
      ],
    },
    related: {
      heading: "Dutch Weave Filter Cloth Variants",
      intro: "Explore our three primary stainless steel dutch weave constructions tailored for specific mechanical load and retention criteria.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Heavy warp wires paired with compacted fine shute wires produce uniform triangular pores for predictable particulate retention and high cake discharge.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Plain Dutch Weave wire cloth",
          },
          badge: "8×85 – 80×400 (to 165×1200)",
          linkLabel: "Explore Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "Twilled warp wire arrangement allows double the weft density with zero direct line of sight, delivering micro-retention down to 1 micron under severe differential pressures.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Twill Dutch Weave wire cloth",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Explore Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dense warp wires paired with heavier spaced weft wires deliver exceptional longitudinal tensile strength for continuous screen belts and high-pressure polymer melt filtration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Reverse Dutch Weave wire cloth",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Explore Reverse Dutch",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "Engineered Filtration Solutions",
      heading: "Specify Stainless Steel Filter Cloth for Your Process",
      lead: "Contact MutualFilter technical specialists for aperture sizing, weave pattern recommendations, and custom fabrication support.",
      secondaryLabel: "Browse Woven Wire Mesh",
      secondaryTo: "/WovenWireMeshPage",
      note: "Material test reports available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "编织丝网系列",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "不锈钢工业滤布",
      lead: "MutualFilter 供应高精度不锈钢荷兰编织滤布，专为严苛固液分离、高压差工况及连续聚合物挤出过滤工程设计。高密度经纬编织产生规则曲折立体微孔通道，实现完全无直射透光的高效颗粒拦截。",
      secondaryCta: {
        label: "浏览编织丝网",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "不锈钢荷兰编织工业滤布卷与网面",
      },
      cardNote: "荷兰编织精密过滤介质",
      cardLink: {
        label: "技术规格表",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "公称精度", value: "1 µm – 310 µm" },
        { label: "编织形式", value: "平纹、斜纹与反向荷兰编织" },
        { label: "标准材质", value: "AISI 304, 304L, 316, 316L" },
        { label: "制造标准", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "结构化微孔过滤介质",
      heading: "精密荷兰编织结构，满足严格微孔截留要求",
      paragraphs: [
        "不锈钢工业滤布（密纹网）采用不等直径的经纬丝与紧密挤压编织工艺。与孔径直接对应两丝之间几何间隙的方孔网不同，荷兰编织迫使流体通过相邻金属丝交叠形成的规则三角形微孔通道。",
        "这种致密的机械结构消除了直通透光开孔，即使在较高的系统运行压差或液压冲击下，也能有效防止颗粒渗透与泄漏。粗经丝所提供的机械刚性能够抵抗介质变形，在多变粘度与周期性反冲洗中保持稳定的孔径尺寸。",
        "MutualFilter 供应工业原卷、定宽精密分切带卷、多层烧结复合滤布及定制激光冲压滤片，广泛配套于叶滤机、烛式滤芯、板框压滤机以及自动换网器等工业过滤设备。",
      ],
      keyPoints: [
        "曲折立体微孔通道实现零直射透光的高效颗粒截留与表面滤饼脱离。",
        "粗经丝配合高密度紧密纬丝结构，在高压差与液压冲击下保持出色的抗形变能力。",
        "支持工业原卷、定宽精密分切带卷以及激光/冲压成型滤片等多种供货形态。",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "不锈钢滤布编织结构微距图",
      },
    },
    specTable: {
      heading: "荷兰编织工业滤布规格概览",
      intro: "主要荷兰编织工业滤布的标准制造参数与常用规格范围。",
      columns: [
        { key: "weaveType", label: "编织类型", align: "left" },
        { key: "meshRange", label: "常用目数范围", align: "right" },
        { key: "retention", label: "公称过滤精度", align: "right" },
        { key: "poreStructure", label: "孔隙特征", align: "left" },
        { key: "standard", label: "执行标准", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "平纹荷兰编织",
          meshRange: "8×85 – 80×400 (最高至 165×1200)",
          retention: "~20 – 310 µm 公称",
          poreStructure: "三角形立体微孔开孔",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "斜纹荷兰编织",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm 公称",
          poreStructure: "2/2 斜纹双丝致密重叠排列",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "反向荷兰编织",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm 公称",
          poreStructure: "紧排细经丝配合高纵向抗拉粗纬丝",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "数据符合 ISO 9044、ASTM E2016 与 ASTM E2814 标准的公称过滤精度与尺寸规范。",
    },
    features: {
      heading: "核心工程优势",
      intro: "工业荷兰编织滤布区别于普通方孔编织网的关键技术特征。",
      items: [
        {
          title: "坚固的抗形变结构",
          text: "粗径基丝构建出高强度的力学骨架，能够在高压差连续运行中保持稳定支撑，杜绝网孔扩张与结构松弛。",
        },
        {
          title: "精确可控的截留精度",
          text: "高精度整经与密实打纬技术，保证整个滤网幅面具有高度一致的微孔几何尺寸，公称拦截范围覆盖 1 µm 至 310 µm。",
        },
        {
          title: "优异的滤饼脱离性能",
          text: "平滑紧致的表面形态使滤饼在反冲洗、机械刮刀刮离或脉冲气流清灰过程中极易完整剥落。",
        },
        {
          title: "优质耐腐蚀不锈钢",
          text: "精选 AISI 304、304L、316 与 316L 工业级不锈钢线材编织，确保在各类腐蚀介质与化学流体中可靠工作。",
        },
      ],
    },
    related: {
      heading: "荷兰编织产品系列",
      intro: "了解 MutualFilter 的三大核心荷兰编织系列，针对不同机械载荷与过滤精度定制设计。",
      cards: [
        {
          title: "平纹荷兰编织",
          text: "粗经丝配合紧密敲紧的细纬丝形成规则三角微孔流道，兼具稳定的固体颗粒拦截精度与良好的表面滤饼剥离特性。",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "平纹荷兰编织工业滤布",
          },
          badge: "8×85 – 80×400 (最高至 165×1200)",
          linkLabel: "查看平纹荷兰编织",
        },
        {
          title: "斜纹荷兰编织",
          text: "结合斜纹走线与双向压紧的极高纬丝密度，形成无直射光透孔的曲折立体通道，在高压差恶劣工况下稳定实现低至 1 微米精密截留。",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "斜纹荷兰编织工业滤布",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "查看斜纹荷兰编织",
        },
        {
          title: "反向荷兰编织",
          text: "高密度紧排细经丝跨越较粗的间距纬丝，提供出色的纵向抗拉强度，专为自动换网器连续过滤带与高温高压聚合物熔体挤出设计。",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "反向荷兰编织工业滤布",
          },
          badge: "72×15 – 325×38",
          linkLabel: "查看反向荷兰编织",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "定制工业过滤方案",
      heading: "为您的工业工艺选配适宜的不锈钢滤布",
      lead: "欢迎联系 MutualFilter 技术工程团队，获取孔径选型、编织结构推荐及精密加工咨询。",
      secondaryLabel: "返回编织丝网总览",
      secondaryTo: "/WovenWireMeshPage",
      note: "材质检验报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Mallas Tejidas",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Tela Filtrante de Acero Inoxidable",
      lead: "MutualFilter suministra tela filtrante de acero inoxidable tejida Dutch de precisión para separación crítica sólido-líquido, alta presión diferencial y extrusión continua de polímeros. Las configuraciones de alta densidad forman canales microtortuosos sin paso de luz directo.",
      secondaryCta: {
        label: "Ver Mallas Tejidas",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "Rollo de tela filtrante de acero inoxidable",
      },
      cardNote: "Medios de filtración Dutch",
      cardLink: {
        label: "Especificaciones",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "Rango nominal", value: "1 µm – 310 µm" },
        { label: "Tipos de tejido", value: "Dutch liso, cruzado e invertido" },
        { label: "Aleaciones estándar", value: "AISI 304, 304L, 316, 316L" },
        { label: "Normas", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "Medios Filtrantes Estructurales",
      heading: "Tejidos Dutch de Precisión para Retención Confiable",
      paragraphs: [
        "La tela filtrante de acero inoxidable emplea diámetros diferenciados en urdimbre y trama con un empaquetamiento de alambres de alta densidad. A diferencia de las mallas cuadradas convencionales, los tejidos Dutch canalizan los fluidos a través de poros triangulares microscópicos formados entre alambres entrelazados.",
        "Esta construcción mecánica elimina las aberturas directas de línea de vista, impidiendo el paso de partículas sólidas incluso bajo presiones operativas elevadas. Los alambres gruesos de urdimbre resisten deformaciones, manteniendo la estabilidad dimensional bajo ciclos hidráulicos continuos.",
        "MutualFilter suministra telas filtrantes en rollos maestros, cintas cortadas a medida, laminados sinterizados y discos troquelados de precisión para filtros de hojas, velas filtrantes y cambiadores continuos de mallas en extrusión.",
      ],
      keyPoints: [
        "Canales microtortuosos proporcionan alta retención de partículas sin paso de luz directo.",
        "Estructura de urdimbre robusta y trama compactada resistente a deformaciones bajo alta presión diferencial.",
        "Disponible en rollos maestros, cintas cortadas a medida y discos filtrantes troquelados de precisión.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Detalle del tejido de tela filtrante de acero inoxidable",
      },
    },
    specTable: {
      heading: "Especificaciones de Telas Filtrantes Dutch",
      intro: "Parámetros estándar de fabricación para telas filtrantes de acero inoxidable en sus configuraciones principales.",
      columns: [
        { key: "weaveType", label: "Tipo de Tejido", align: "left" },
        { key: "meshRange", label: "Rango de Malla", align: "right" },
        { key: "retention", label: "Retención Nominal", align: "right" },
        { key: "poreStructure", label: "Geometría de Poros", align: "left" },
        { key: "standard", label: "Norma", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "Tejido Dutch Liso",
          meshRange: "8×85 – 80×400 (hasta 165×1200)",
          retention: "~20 – 310 µm nominal",
          poreStructure: "Poros triangulares micrométricos regulares",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "Malla Twill Dutch",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm nominal",
          poreStructure: "Disposición sarga 2/2 compacta de doble trama",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "Malla Reps Inversa",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm nominal",
          poreStructure: "Urdimbre densa con alta resistencia a tracción longitudinal",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "Valores conformes con normas ISO 9044, ASTM E2016 y ASTM E2814.",
    },
    features: {
      heading: "Características Técnicas Clave",
      intro: "Propiedades mecánicas y estructurales que diferencian las telas filtrantes Dutch.",
      items: [
        {
          title: "Rigidez Estructural Superior",
          text: "Los alambres robustos garantizan una base mecánica estable que soporta diferenciales de presión severos sin distorsión de poros.",
        },
        {
          title: "Retención Homogénea",
          text: "La densidad uniforme en el telar asegura aberturas micrométricas constantes desde 1 µm hasta 310 µm.",
        },
        {
          title: "Fácil Descarga de Torta",
          text: "La superficie plana y compacta facilita el desprendimiento de sólidos acumulados durante el retrolavado.",
        },
        {
          title: "Aleaciones de Alta Calidad",
          text: "Fabricada en aceros inoxidables AISI 304, 304L, 316 y 316L para compatibilidad con fluidos industriales corrosivos.",
        },
      ],
    },
    related: {
      heading: "Variantes de Tejido Dutch",
      intro: "Conozca los tres tipos de tejido Dutch desarrollados para diversas exigencias de presión y filtración.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Hilos de urdimbre gruesos con alambres de trama fina compactada forman poros triangulares uniformes para retención predecible y descarga de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tela Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (hasta 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "La disposición en sarga duplica la densidad de trama sin paso de luz directo, ofreciendo micro-retención de hasta 1 micra bajo severas presiones diferenciales.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Tela Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdidumbre de alta densidad combinada con alambres de trama más gruesos ofrece gran resistencia a la tracción para cambiadores de pantalla continuos y extrusión.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Tela Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "Soluciones de Filtración B2B",
      heading: "Consulte con Especialistas en Tela Filtrante",
      lead: "Contáctenos para asistencia técnica en la selección de aperturas, patrones de tejido y fabricación a medida.",
      secondaryLabel: "Volver a Mallas Tejidas",
      secondaryTo: "/WovenWireMeshPage",
      note: "Informes de prueba de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Drahtgewebe",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Filtertuch aus Edelstahl",
      lead: "MutualFilter liefert Präzisions-Tressengewebe aus Edelstahl für anspruchsvolle Fest-Flüssig-Trennung, hohe Differenzdrücke und kontinuierliche Polymerextrusion. Dichte Drahtstrukturen erzeugen gewundene Mikroporen ohne Direktdurchsicht.",
      secondaryCta: {
        label: "Drahtgewebe ansehen",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "Edelstahl Filtertuch Rolle und Gewebeoberfläche",
      },
      cardNote: "Tressengewebe-Filtermedien",
      cardLink: {
        label: "Spezifikationen",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 310 µm" },
        { label: "Webarten", value: "Glatte, Köper- & Panzertresse" },
        { label: "Standardlegierungen", value: "AISI 304, 304L, 316, 316L" },
        { label: "Standards", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "Strukturelle Filtermedien",
      heading: "Präzise Tressengewebe für anspruchsvolle Trennverfahren",
      paragraphs: [
        "Industrielles Edelstahl-Filtertuch (Tressengewebe) kombiniert unterschiedliche Kett- und Schussdrahtdurchmesser mit extrem dichter Bindung. Im Gegensatz zu Standard-Quadratmaschengeweben strömt das Medium durch gewundene, dreieckige Porenkanäle zwischen den Metalldrähten.",
        "Diese Bauweise verhindert Direktdurchsicht und gewährleistet hohe Rückhalteraten selbst bei wechselnden hydraulischen Drücken. Starke Kettendrähte verleihen dem Gewebe eine hohe mechanische Stabilität.",
        "MutualFilter liefert Filtertücher als Mutterrollen, präzise geschnittene Streifen, mehrlagige Sinterverbunde und gestanzte Filterscheiben für Blattfilter, Kerzenfilter und Siebwechsler.",
      ],
      keyPoints: [
        "Gewundene Mikrokanäle bieten hohe Partikelrückhaltung ohne Direktdurchsicht.",
        "Starke Kette und verdichtete Schusskonstruktion widerstehen Verformung unter hohem Differenzdruck.",
        "Lieferbar in Mutterrollen, präzisen Spaltbändern und gestanzten Filterscheiben.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Nahaufnahme Edelstahl Tressengewebe",
      },
    },
    specTable: {
      heading: "Spezifikationsübersicht Tressengewebe",
      intro: "Standardparameter für industrielle Edelstahl-Tressengewebe der gängigen Webarten.",
      columns: [
        { key: "weaveType", label: "Webart", align: "left" },
        { key: "meshRange", label: "Maschenbereich", align: "right" },
        { key: "retention", label: "Filterfeinheit", align: "right" },
        { key: "poreStructure", label: "Porenstruktur", align: "left" },
        { key: "standard", label: "Standards", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "Glatte Tresse (Plain Dutch)",
          meshRange: "8×85 – 80×400 (bis 165×1200)",
          retention: "~20 – 310 µm nominal",
          poreStructure: "Gleichmäßige dreieckige Mikroporen",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "Köpertresse (Twill Dutch)",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm nominal",
          poreStructure: "2/2 dichte Köperbindung mit doppelter Schussdichte",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "Panzertresse (Reverse Dutch)",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm nominal",
          poreStructure: "Dichte Kette mit extremer Längszugfestigkeit",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "Werte gemäß ISO 9044, ASTM E2016 und ASTM E2814.",
    },
    features: {
      heading: "Technische Merkmale",
      intro: "Konstruktive Vorzüge von Edelstahl-Tressengeweben im industriellen Einsatz.",
      items: [
        {
          title: "Hohe Formstabilität",
          text: "Kräftige Basisdrähte verhindern ein Aufweiten der Poren bei hohen Druckgradienten.",
        },
        {
          title: "Exakte Trenngrenzen",
          text: "Präzises Schlagen der Schussdrähte garantiert reproduzierbare Filterfeinheiten von 1 µm bis 310 µm.",
        },
        {
          title: "Effizienter Filterkuchenabwurf",
          text: "Glatte Oberflächen erleichtern die Abreinigung bei Rückspülvorgängen.",
        },
        {
          title: "Korrosionsfeste Legierungen",
          text: "Hergestellt aus AISI 304, 304L, 316 und 316L für dauerhaften Einsatz in aggressiven Medien.",
        },
      ],
    },
    related: {
      heading: "Tressengewebe-Ausführungen",
      intro: "Entdecken Sie unsere drei Tressengewebe-Typen für unterschiedliche Druck- und Separationsanforderungen.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Starke Kettendrähte und dicht geschlagene feine Schussdrähte bilden eine einheitliche dreieckige Porengeometrie für zuverlässigen Rückhalt und leichten Kuchenabwurf.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Glatte Tresse Filtergewebe",
          },
          badge: "8×85 – 80×400 (bis 165×1200)",
          linkLabel: "Details zu Glatte Tresse",
        },
        {
          title: "Twill Dutch Weave",
          text: "Die köperbindige Kettanordnung verdoppelt die Schussdichte ohne Direktdurchsicht und bietet Feinstfiltration ab 1 Mikrometer unter hohem Differenzdruck.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Köpertresse Filtergewebe",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Details zu Köpertresse",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Dichte Kettendrähte gepaart mit stärkeren Schussdrähten bieten extreme Längszugfestigkeit für kontinuierliche Siebbänder und Hochdruck-Polymerschmelzefiltration.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Panzertresse Filtergewebe",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Details zu Panzertresse",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "Filtrationstechnik nach Maß",
      heading: "Passendes Filtertuch für Ihre Anlage auswählen",
      lead: "Nutzen Sie die Unterstützung unserer Anwendungsexperten für Gewebeauslegung und Konfektionierung.",
      secondaryLabel: "Zurück zu Drahtgewebe",
      secondaryTo: "/WovenWireMeshPage",
      note: "Werksprüfzeugnisse auf Anfrage bei Auftragserteilung erhältlich.",
    },
  },
  fr: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile Métallique",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Toile Filtrante en Acier Inoxydable",
      lead: "MutualFilter fournit des toiles filtrantes inox tissées reps de précision pour la séparation critique solide-liquide, les environnements à forte pression différentielle et l'extrusion continue de polymères. Canaux micro-tortueux assurant une forte rétention sans ligne de visée directe.",
      secondaryCta: {
        label: "Voir Toiles Tissées",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "Rouleau de toile filtrante métallique inox",
      },
      cardNote: "Milieux filtrants reps",
      cardLink: {
        label: "Spécifications",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "Seuil nominal", value: "1 µm – 310 µm" },
        { label: "Types d'armure", value: "Reps uni, sergé et inversé" },
        { label: "Alliages standard", value: "AISI 304, 304L, 316, 316L" },
        { label: "Normes", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "Milieux Filtrants Structurés",
      heading: "Tissages Reps de Haute Précision pour Rétention Fine",
      paragraphs: [
        "La toile filtrante inox utilise des diamètres de fil asymétriques entre chaîne et trame avec un serrage dense. Contrairement aux mailles carrées, les toiles reps forcent le fluide à traverser des canaux triangulaires microscopiques formés par le chevauchement des fils.",
        "Cette structure mécanique supprime les ouvertures directes, évitant le passage intempestif des particules solides sous forte pression d'utilisation. Les gros fils de chaîne confèrent une stabilité dimensionnelle élevée face aux variations hydrauliques.",
        "MutualFilter fournit des toiles en rouleaux industriels, bandes refendues de précision, composites frittés multicouches et disques découpés pour filtres à feuilles, cartouches bougies et changeurs de filtres d'extrusion.",
      ],
      keyPoints: [
        "Canaux micro-tortueux assurant une forte rétention de particules sans ligne de visée directe.",
        "Structure à chaîne robuste et trame serrée résistante à la déformation sous forte pression différentielle.",
        "Disponible en rouleaux mères, bandes refendues sur mesure et disques filtrants découpés de précision.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Vue macro de toile filtrante inox",
      },
    },
    specTable: {
      heading: "Spécifications des Toiles Filtrantes Reps",
      intro: "Paramètres de fabrication standards pour toiles filtrantes en acier inoxydable selon l'armure.",
      columns: [
        { key: "weaveType", label: "Type d'Armure", align: "left" },
        { key: "meshRange", label: "Plage de Maillage", align: "right" },
        { key: "retention", label: "Seuil Nominal", align: "right" },
        { key: "poreStructure", label: "Structure des Pores", align: "left" },
        { key: "standard", label: "Norme", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "Tissage Reps Uni (Plain Dutch)",
          meshRange: "8×85 – 80×400 (jusqu'à 165×1200)",
          retention: "~20 – 310 µm nominal",
          poreStructure: "Pores triangulaires micrométriques réguliers",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "Toile Twill Dutch (Reps Sergé)",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm nominal",
          poreStructure: "Armure sergée 2/2 à double densité de trame",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "Toile Reps Inversé (Reverse Dutch)",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm nominal",
          poreStructure: "Chaîne dense à haute résistance à la traction",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "Spécifications conformes aux normes ISO 9044, ASTM E2016 et ASTM E2814.",
    },
    features: {
      heading: "Atouts Techniques Essentiels",
      intro: "Points forts des toiles métalliques reps pour la filtration industrielle.",
      items: [
        {
          title: "Rigidité Mécanique Élevée",
          text: "Les fils d'armature soutiennent les contraintes mécaniques et évitent l'élargissement des ouvertures sous charge.",
        },
        {
          title: "Rétention Calibrée",
          text: "Le tissage régulier garantit des coupures de filtration uniformes de 1 µm à 310 µm.",
        },
        {
          title: "Décolmatage Facilité",
          text: "La surface compacte favorise l'évacuation propre du gâteau de filtration lors des rétro-lavages.",
        },
        {
          title: "Alliages Inox Résistants",
          text: "Disponible en nuances AISI 304, 304L, 316 et 316L pour la résistance chimique.",
        },
      ],
    },
    related: {
      heading: "Familles de Toiles Reps",
      intro: "Découvrez nos trois tissages reps adaptés aux contraintes spécifiques de filtration industrielle.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fils de chaîne robustes associés à une trame fine serrée formant des pores triangulaires réguliers pour une rétention prévisible et un démoulage aisé du gâteau.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Toile Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (jusqu'à 165×1200)",
          linkLabel: "Découvrir Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "L'armure sergée permet de doubler la densité de trame sans passage direct de lumière, assurant une filtration fine dès 1 micron sous forte pression différentielle.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Toile Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Découvrir Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Fils de chaîne denses combinés à des trames plus épaisses pour une résistance longitudinale élevée sur bandes de changeurs continus et filtration de polymères.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Toile Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Découvrir Reverse Dutch",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "Étude et Devis Technique",
      heading: "Consultez MutualFilter pour Vos Toiles Filtrantes",
      lead: "Nos spécialistes vous orientent sur le dimensionnement des pores et la configuration de tissage.",
      secondaryLabel: "Retour aux Toiles Métalliques",
      secondaryTo: "/WovenWireMeshPage",
      note: "Certificats matière disponibles lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "FilterCloth",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Telas Tecidas",
      standardBadge: "ISO 9044 / ASTM E2814",
      heading: "Tecido Filtrante de Aço Inoxidável",
      lead: "A MutualFilter fornece tecidos filtrantes de aço inoxidável em trama holandesa de precisão para separação sólido-líquido crítica, alta pressão diferencial e extrusão contínua de polímeros. Microcanais tortuosos oferecem alta retenção sem passagem direta de luz.",
      secondaryCta: {
        label: "Ver Telas Tecidas",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp?v=djofi647st4",
        alt: "Rolo de tecido filtrante de aço inoxidável",
      },
      cardNote: "Meios filtrantes holandeses",
      cardLink: {
        label: "Especificações",
        to: "/FilterClothPage#specifications",
      },
      specs: [
        { label: "Faixa nominal", value: "1 µm – 310 µm" },
        { label: "Tipos de trama", value: "Holandesa lisa, sarja e reversa" },
        { label: "Ligas padrão", value: "AISI 304, 304L, 316, 316L" },
        { label: "Normas", value: "ISO 9044 / ASTM E2814" },
      ],
    },
    overview: {
      eyebrow: "Meios Filtrantes Estruturados",
      heading: "Tecidos Holandeses de Alta Precisão para Retenção Eficaz",
      paragraphs: [
        "O tecido filtrante de aço inoxidável utiliza fios de urdume e trama com diâmetros distintos e alta densidade de montagem. Diferente das telas quadradas convencionais, o tecido holandês força o fluxo do fluido por canais triangulares microscópicos.",
        "Essa geometria mecânica elimina furos diretos de linha reta, prevenindo a passagem indesejada de partículas mesmo em pressões diferenciais elevadas. Os fios reforçados de urdume sustentam o tecido contra deformações estruturais.",
        "A MutualFilter fornece tecidos filtrantes em bobinas mestre, fitas cortadas sob medida, telas sinterizadas multicamadas e discos estampados para filtros folha, velas e trocadores contínuos de tela de polímero.",
      ],
      keyPoints: [
        "Microcanais tortuosos oferecem alta retenção de partículas sem passagem direta de luz.",
        "Estrutura de urdume reforçada e trama compactada resistente à deformação sob alta pressão diferencial.",
        "Disponível em bobinas mestre, fitas cortadas sob medida e discos filtrantes estampados de precisão.",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
        alt: "Detalhe em macro do tecido filtrante de aço inoxidável",
      },
    },
    specTable: {
      heading: "Especificações de Tecidos Filtrantes Holandeses",
      intro: "Parâmetros industriais de fabricação para tecidos filtrantes de aço inoxidável nas principais tramas.",
      columns: [
        { key: "weaveType", label: "Tipo de Trama", align: "left" },
        { key: "meshRange", label: "Faixa de Malha", align: "right" },
        { key: "retention", label: "Retenção Nominal", align: "right" },
        { key: "poreStructure", label: "Estrutura dos Poros", align: "left" },
        { key: "standard", label: "Normas", align: "right" },
      ],
      rows: [
        {
          id: "row-pd",
          weaveType: "Trama Holandesa Lisa",
          meshRange: "8×85 – 80×400 (até 165×1200)",
          retention: "~20 – 310 µm nominal",
          poreStructure: "Poros triangulares micrométricos regulares",
          standard: "ISO 9044 / ASTM E2814",
        },
        {
          id: "row-td",
          weaveType: "Trama Holandesa Sarjada",
          meshRange: "20×250 – 400×2800",
          retention: "~1 – 225 µm nominal",
          poreStructure: "Trama sarjada 2/2 compacta com dupla densidade",
          standard: "ISO 9044 / ASTM E2016",
        },
        {
          id: "row-rd",
          weaveType: "Trama Holandesa Reversa",
          meshRange: "72×15 – 325×38",
          retention: "10 – 300 µm nominal",
          poreStructure: "Urdume denso com alta resistência à tração longitudinal",
          standard: "ISO 9044 / ASTM E2016",
        },
      ],
      caption: "Conformidade com os padrões ISO 9044, ASTM E2016 e ASTM E2814.",
    },
    features: {
      heading: "Destaques Construtivos",
      intro: "Propriedades funcionais do tecido filtrante em aplicações industriais contínuas.",
      items: [
        {
          title: "Rigidez Mecânica",
          text: "Estrutura de fios encorpados que resiste a gradientes de pressão acentuados sem alargar a abertura de filtração.",
        },
        {
          title: "Retenção Homogênea",
          text: "Tecelagem de precisão que assegura limites de corte de 1 µm até 310 µm ao longo de toda a bobina.",
        },
        {
          title: "Excelente Remoção de Torta",
          text: "Superfície suave e densa que permite desprendimento rápido de sólidos durante a retrolavagem.",
        },
        {
          title: "Ligas de Alta Durabilidade",
          text: "Fabricação com fios em aços inoxidáveis AISI 304, 304L, 316 e 316L para ampla compatibilidade.",
        },
      ],
    },
    related: {
      heading: "Modelos de Tecido Holandês",
      intro: "Conheça nossas opções em tramas holandesas desenvolvidas para demandas mecânicas variadas.",
      cards: [
        {
          title: "Plain Dutch Weave",
          text: "Fios de urdume grossos combinados com trama fina compactada produzem geometria uniforme de poros triangulares para retenção previsível e fácil descarte de torta.",
          to: "/PlainDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-dutch-weave.webp",
            alt: "Tecido Plain Dutch Weave",
          },
          badge: "8×85 – 80×400 (até 165×1200)",
          linkLabel: "Ver Plain Dutch",
        },
        {
          title: "Twill Dutch Weave",
          text: "A urdidura em sarja dobra a densidade da trama sem linha de visão direta, garantindo microrretenção a partir de 1 mícron sob pressões diferenciais severas.",
          to: "/TwillDutchWeavePage",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/twill-dutch-weave.webp",
            alt: "Tecido Twill Dutch Weave",
          },
          badge: "20×250 – 400×2800",
          linkLabel: "Ver Twill Dutch",
        },
        {
          title: "Reverse Dutch Weave",
          text: "Urdumes densos combinados com arames de trama mais grossos proporcionam extrema resistência à tração para fitas contínuas e filtração de polímeros sob alta pressão.",
          to: "/ReverseDutchWeavePage",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-reverse-dutch-52764d.png",
            alt: "Tecido Reverse Dutch Weave",
          },
          badge: "72×15 – 325×38",
          linkLabel: "Ver Reverse Dutch",
        },
      ],
    },
    cta: {
      id: "filter-cloth-cta",
      eyebrow: "Engenharia de Filtragem B2B",
      heading: "Consulte a MutualFilter para Tecidos Filtrantes",
      lead: "Nossa equipe técnica auxilia na especificação de aberturas, padrão de tecelagem e corte customizado.",
      secondaryLabel: "Voltar para Telas Tecidas",
      secondaryTo: "/WovenWireMeshPage",
      note: "Relatórios de ensaio de material disponíveis mediante solicitação no pedido.",
    },
  },
};
